import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { readListState, persistListState, useListScroll } from '../lib/listState';
import api from '../utils/api';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import { ShoppingCart, Plus, PackageCheck, Package, Sparkles, Archive, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { formatCurrency, formatDate } from '../lib/constants';
import ReceivePRDialog from '../components/purchasing/ReceivePRDialog';

const PR_HISTORY_STATUSES = ['received', 'rejected'];
const HIGH_VALUE_THRESHOLD = 5_000_000;

const PR_STATUSES = {
  pending: { label: 'Menunggu Approval', color: 'bg-amber-100 text-amber-700' },
  approved: { label: 'Disetujui', color: 'bg-blue-100 text-blue-700' },
  ordered: { label: 'Dipesan ke Vendor', color: 'bg-indigo-100 text-indigo-700' },
  received: { label: 'Barang Diterima', color: 'bg-green-100 text-green-700' },
  rejected: { label: 'Ditolak', color: 'bg-red-100 text-red-700' },
};

const PurchasingPage = () => {
  const { user, hasPermission } = useAuth();
  const savedListState = useRef(readListState('purchasing')).current;
  const [requests, setRequests] = useState([]);
  const [items, setItems] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [selectedPR, setSelectedPR] = useState(null);
  const [showUpdateDialog, setShowUpdateDialog] = useState(false);
  const [showReceiveDialog, setShowReceiveDialog] = useState(false);
  const [createForm, setCreateForm] = useState({
    ticket_id: '',
    item_id: '',
    quantity: 1,
    reason: '',
    vendor: '',
    estimated_price: '',
    // Custom (non-master) item fields
    is_custom: false,
    custom_name: '',
    custom_unit: 'pcs',
    custom_category: 'lainnya',
    custom_price: '',
  });
  const [updateForm, setUpdateForm] = useState({ status: '', vendor: '', price: '' });
  const [receiveForm, setReceiveForm] = useState({ receive_location: 'PUSAT', actual_price: '', actual_quantity: '', notes: '', receipt_url: '', receipt_filename: '' });
  const [uploadingReceipt, setUploadingReceipt] = useState(false);
  const [aiEstimating, setAiEstimating] = useState(false);
  const [activeTab, setActiveTab] = useState(savedListState.activeTab || 'active');
  const [searchQuery, setSearchQuery] = useState(savedListState.searchQuery || '');
  const [filterLocation, setFilterLocation] = useState(savedListState.filterLocation || 'all');
  const [dateFrom, setDateFrom] = useState(savedListState.dateFrom || '');
  const [dateTo, setDateTo] = useState(savedListState.dateTo || '');

  const handleAiEstimate = async () => {
    if (!createForm.custom_name.trim()) {
      toast.error('Isi nama barang dulu untuk estimasi AI');
      return;
    }
    setAiEstimating(true);
    try {
      const { data } = await api.post('/tickets/estimate-cost', {
        category: 'umum',
        title: `Estimasi harga: ${createForm.custom_name}`,
        description: `Berikan estimasi harga PER UNIT untuk 1 ${createForm.custom_unit || 'pcs'} ${createForm.custom_name} berdasarkan harga pasar Indonesia saat ini. Kategori: ${createForm.custom_category}.`,
      });
      const estimated = parseInt(data.estimated_cost || 0);
      if (estimated > 0) {
        setCreateForm((f) => {
          const qty = parseInt(f.quantity) || 1;
          return { ...f, custom_price: String(estimated), estimated_price: String(estimated * qty) };
        });
        toast.success(`Estimasi AI: Rp ${estimated.toLocaleString('id-ID')}`, {
          description: data.reasoning || '',
        });
      } else {
        toast.error('AI tidak berhasil mengestimasi harga');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal estimasi AI');
    } finally {
      setAiEstimating(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    persistListState('purchasing', { activeTab, searchQuery, filterLocation, dateFrom, dateTo });
  }, [activeTab, searchQuery, filterLocation, dateFrom, dateTo]);

  useListScroll('purchasing', !loading);

  // Auto-calc estimated_price = qty * unit price when picking a master item OR filling custom price
  useEffect(() => {
    const qty = parseInt(createForm.quantity) || 0;
    let unitPrice = 0;
    if (createForm.is_custom) {
      unitPrice = parseFloat(createForm.custom_price) || 0;
    } else if (createForm.item_id) {
      const item = items.find((i) => i.id === createForm.item_id);
      if (item) unitPrice = item.price || 0;
    }
    if (!unitPrice) return;
    const total = qty * unitPrice;
    setCreateForm((f) => (
      String(f.estimated_price) === String(total) ? f : { ...f, estimated_price: String(total) }
    ));
  }, [createForm.item_id, createForm.quantity, createForm.is_custom, createForm.custom_price, items]);

  const loadData = async () => {
    try {
      const [prRes, itemsRes, ticketsRes, locsRes] = await Promise.all([
        api.get('/purchasing/requests'),
        api.get('/items', { params: { include_all: true } }),
        api.get('/tickets-simple'),
        api.get('/locations'),
      ]);
      setRequests(prRes.data);
      setItems(itemsRes.data);
      setTickets(ticketsRes.data);
      setLocations(locsRes.data);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    // Validation depending on mode
    if (createForm.is_custom) {
      if (!createForm.custom_name.trim()) {
        toast.error('Nama barang custom harus diisi');
        return;
      }
      if (!createForm.custom_price || parseFloat(createForm.custom_price) <= 0) {
        toast.error('Estimasi harga barang harus lebih dari 0');
        return;
      }
    } else if (!createForm.item_id) {
      toast.error('Pilih barang dulu');
      return;
    }
    if (!createForm.vendor.trim()) {
      toast.error('Nama vendor wajib diisi');
      return;
    }
    if (!createForm.estimated_price || parseFloat(createForm.estimated_price) <= 0) {
      toast.error('Harga estimasi pembelian wajib diisi (lebih dari 0)');
      return;
    }
    try {
      let itemIdToUse = createForm.item_id;

      if (createForm.is_custom) {
        const { data: newItem } = await api.post('/items?source=pr', {
          name: createForm.custom_name.trim(),
          category: createForm.custom_category,
          unit: createForm.custom_unit,
          price: parseFloat(createForm.custom_price),
          min_stock: 0,
        });
        itemIdToUse = newItem.id;
        toast.success(`Barang baru "${newItem.name}" ditambahkan ke master`);
      }

      const ticketIdValue = createForm.ticket_id && createForm.ticket_id !== '_none' ? createForm.ticket_id : null;
      await api.post('/purchasing/requests', {
        ticket_id: ticketIdValue,
        item_id: itemIdToUse,
        quantity: parseInt(createForm.quantity),
        reason: createForm.reason,
        vendor: createForm.vendor.trim(),
        estimated_price: parseFloat(createForm.estimated_price),
      });
      toast.success('Purchase request dibuat');
      setShowCreateDialog(false);
      setCreateForm({
        ticket_id: '', item_id: '', quantity: 1, reason: '',
        vendor: '', estimated_price: '',
        is_custom: false, custom_name: '', custom_unit: 'pcs',
        custom_category: 'lainnya', custom_price: '',
      });
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal buat PR');
    }
  };

  const [approvingPR, setApprovingPR] = useState(null);
  const [approveForm, setApproveForm] = useState({ quantity: '', estimated_price: '', note: '' });

  const openApproveDialog = (pr) => {
    setApprovingPR(pr);
    // Pre-fill from PR: prefer submitter's estimated_price; fallback to qty × unit_price/custom_price
    let est = pr.estimated_price;
    if (!est || est <= 0) {
      const qty = pr.quantity || 1;
      const unit = pr.price || pr.custom_price || pr.item_price || 0;
      est = qty * unit;
    }
    setApproveForm({
      quantity: String(pr.quantity || 1),
      estimated_price: String(est || 0),
      note: '',
    });
  };

  const handleApprovePR = async (pr) => {
    // Open editable dialog
    openApproveDialog(pr);
  };

  const submitApprove = async () => {
    if (!approvingPR) return;
    const qty = parseInt(approveForm.quantity);
    const price = parseFloat(approveForm.estimated_price);
    if (!qty || qty <= 0) {
      toast.error('Jumlah harus > 0');
      return;
    }
    try {
      await api.post(`/purchasing/requests/${approvingPR.id}/approve`, {
        quantity: qty,
        estimated_price: isNaN(price) ? undefined : price,
        note: approveForm.note.trim() || undefined,
      });
      toast.success('PR disetujui');
      setApprovingPR(null);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal approve');
    }
  };

  const handleRejectPR = async (pr) => {
    const reason = window.prompt('Alasan penolakan PR:');
    if (!reason || !reason.trim()) return;
    try {
      await api.post(`/purchasing/requests/${pr.id}/reject`, { reason });
      toast.success('PR ditolak');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal reject');
    }
  };

  const handleMarkOrdered = async (pr) => {
    if (!window.confirm(`Tandai sudah dipesan ke ${pr.vendor || 'vendor'}?`)) return;
    try {
      await api.post(`/purchasing/requests/${pr.id}/mark-ordered`);
      toast.success('PR ditandai sudah dipesan');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal');
    }
  };

  const handleDeletePR = async (pr) => {
    if (!window.confirm('Hapus PR ini? Aksi tidak dapat dibatalkan.')) return;
    try {
      await api.delete(`/purchasing/requests/${pr.id}`);
      toast.success('PR dihapus');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal hapus');
    }
  };

  const openUpdateDialog = (pr) => {
    setSelectedPR(pr);
    setUpdateForm({ status: pr.status, vendor: pr.vendor || '', price: pr.price || '' });
    setShowUpdateDialog(true);
  };

  const openReceiveDialog = (pr) => {
    setSelectedPR(pr);
    setReceiveForm({
      receive_location: 'PUSAT',
      actual_price: pr.price || '',
      actual_quantity: String(pr.quantity || ''),
      notes: '',
      receipt_url: '',
      receipt_filename: '',
    });
    setShowReceiveDialog(true);
  };

  const handleUploadReceiveReceipt = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) { toast.error('Ukuran file maksimal 8MB'); return; }
    setUploadingReceipt(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await api.post('/uploads/receipt', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setReceiveForm((f) => ({ ...f, receipt_url: data.url, receipt_filename: data.filename }));
      toast.success('Tanda terima diunggah');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal upload');
    } finally {
      setUploadingReceipt(false);
      e.target.value = '';
    }
  };

  const handleUpdate = async () => {
    try {
      const params = new URLSearchParams({
        status: updateForm.status,
        ...(updateForm.vendor && { vendor: updateForm.vendor }),
        ...(updateForm.price && { price: updateForm.price }),
      });
      await api.put(`/purchasing/requests/${selectedPR.id}?${params.toString()}`);
      toast.success('Purchase request diperbarui');
      setShowUpdateDialog(false);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal memperbarui');
    }
  };

  const handleReceive = async () => {
    try {
      const actualQtyInt = parseInt(receiveForm.actual_quantity);
      if (!actualQtyInt || actualQtyInt <= 0) {
        toast.error('Jumlah aktual harus lebih dari 0');
        return;
      }
      const payload = {
        receive_location: receiveForm.receive_location,
        notes: receiveForm.notes,
        receipt_url: receiveForm.receipt_url || null,
        receipt_filename: receiveForm.receipt_filename || null,
        actual_quantity: actualQtyInt,
      };
      if (receiveForm.actual_price) {
        payload.actual_price = parseFloat(receiveForm.actual_price);
      }
      await api.post(`/purchasing/requests/${selectedPR.id}/receive`, payload);
      toast.success(`Barang diterima. Stok ${receiveForm.receive_location} bertambah ${actualQtyInt}`);
      setShowReceiveDialog(false);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal terima barang');
    }
  };

  const canApprovePR = hasPermission('approve_purchase_request');
  const canOrderPR = hasPermission('order_purchase_request');
  const canReceive = hasPermission('receive_purchase_request');
  const canCreatePR = hasPermission('submit_purchase_request');
  const canApproveHighValue = hasPermission('approve_high_value');

  const isHighValuePR = (pr) => {
    const est = pr.estimated_price || (pr.price || 0) * (pr.quantity || 0);
    return est > HIGH_VALUE_THRESHOLD;
  };

  const canDeletePR = (pr) => {
    if (!user) return false;
    if (user.role === 'super_admin') return true;
    // Pending: creator can cancel their own
    if (pr.status === 'pending' && pr.requested_by === user.id) return true;
    return false;
  };

  return (
    <Layout>
      <div className="space-y-6" data-testid="purchasing-page">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <p className="text-xs uppercase tracking-[0.2em] font-bold text-muted-foreground mb-2">Purchasing</p>
            <h1 className="text-3xl sm:text-4xl font-medium tracking-tight">Purchase Request</h1>
            <p className="text-muted-foreground mt-1 text-sm">Beli barang untuk restock gudang atau kebutuhan tiket</p>
          </div>
          {canCreatePR && (
            <Button
              onClick={() => setShowCreateDialog(true)}
              className="bg-primary hover:bg-primary/90"
              data-testid="create-pr-button"
            >
              <Plus className="w-4 h-4 mr-2" />
              Buat PR Baru
            </Button>
          )}
        </div>

        {/* Info card - workflow guide */}
        <Card className="border-primary/20 bg-primary/5">
          <CardContent className="p-4">
            <p className="text-sm font-semibold mb-2">Alur Purchasing:</p>
            <div className="grid grid-cols-1 sm:grid-cols-4 gap-3 text-xs text-muted-foreground">
              <div className="flex items-start gap-2">
                <span className="w-5 h-5 rounded-full bg-primary text-primary-foreground text-xs flex items-center justify-center flex-shrink-0 font-bold">1</span>
                <span><strong className="text-foreground">Buat PR</strong> — tanpa tiket (restock) atau linked ke tiket</span>
              </div>
              <div className="flex items-start gap-2">
                <span className="w-5 h-5 rounded-full bg-primary text-primary-foreground text-xs flex items-center justify-center flex-shrink-0 font-bold">2</span>
                <span><strong className="text-foreground">Approve</strong> & isi vendor + harga</span>
              </div>
              <div className="flex items-start gap-2">
                <span className="w-5 h-5 rounded-full bg-primary text-primary-foreground text-xs flex items-center justify-center flex-shrink-0 font-bold">3</span>
                <span><strong className="text-foreground">Order</strong> ke vendor</span>
              </div>
              <div className="flex items-start gap-2">
                <span className="w-5 h-5 rounded-full bg-primary text-primary-foreground text-xs flex items-center justify-center flex-shrink-0 font-bold">4</span>
                <span><strong className="text-foreground">Terima</strong> → stok otomatis tambah ke lokasi</span>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-border">
          <CardHeader className="border-b border-border">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <CardTitle className="text-xl tracking-tight font-medium">Daftar Purchase Request</CardTitle>
              <Tabs value={activeTab} onValueChange={setActiveTab}>
                <TabsList data-testid="pr-tabs">
                  <TabsTrigger value="active" data-testid="pr-tab-active">
                    <ShoppingCart className="w-4 h-4 mr-2" />
                    Aktif ({requests.filter((r) => !PR_HISTORY_STATUSES.includes(r.status)).length})
                  </TabsTrigger>
                  <TabsTrigger value="history" data-testid="pr-tab-history">
                    <Archive className="w-4 h-4 mr-2" />
                    Riwayat ({requests.filter((r) => PR_HISTORY_STATUSES.includes(r.status)).length})
                  </TabsTrigger>
                </TabsList>
              </Tabs>
            </div>
          </CardHeader>
          <div className="p-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 border-b border-border bg-secondary/20">
            <div>
              <Label className="text-xs">Cari (barang / vendor / tiket)</Label>
              <Input
                placeholder="Ketik untuk mencari..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                data-testid="pr-search-input"
                className="h-9"
              />
            </div>
            <div>
              <Label className="text-xs">Cabang (via tiket)</Label>
              <Select value={filterLocation} onValueChange={setFilterLocation}>
                <SelectTrigger className="h-9" data-testid="pr-filter-location">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Semua Cabang</SelectItem>
                  {locations.map((l) => (
                    <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Dari Tanggal</Label>
              <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="h-9" data-testid="pr-filter-from" />
            </div>
            <div>
              <Label className="text-xs">Sampai</Label>
              <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="h-9" data-testid="pr-filter-to" />
            </div>
          </div>
          <CardContent className="p-0">
            {(() => {
              const ticketLocByNumber = Object.fromEntries(tickets.map((t) => [t.ticket_number, t.location_id]));
              const q = searchQuery.trim().toLowerCase();
              const fromTs = dateFrom ? new Date(`${dateFrom}T00:00:00`).getTime() : null;
              const toTs = dateTo ? new Date(`${dateTo}T23:59:59`).getTime() : null;
              const filteredPRs = requests.filter((r) => {
                if (activeTab === 'history' ? !PR_HISTORY_STATUSES.includes(r.status) : PR_HISTORY_STATUSES.includes(r.status)) return false;
                if (q) {
                  const hay = `${r.item_name || ''} ${r.vendor || ''} ${r.ticket_id || ''} ${r.reason || ''}`.toLowerCase();
                  if (!hay.includes(q)) return false;
                }
                if (filterLocation !== 'all') {
                  const prLoc = r.ticket_id ? ticketLocByNumber[r.ticket_id] : null;
                  if (prLoc !== filterLocation) return false;
                }
                if (fromTs || toTs) {
                  const ts = r.created_at ? new Date(r.created_at).getTime() : 0;
                  if (fromTs && ts < fromTs) return false;
                  if (toTs && ts > toTs) return false;
                }
                return true;
              });
              if (loading) {
                return (
                  <div className="p-12 text-center">
                    <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-primary border-r-transparent"></div>
                  </div>
                );
              }
              if (filteredPRs.length === 0) {
                return (
                  <div className="p-12 text-center text-muted-foreground">
                    <ShoppingCart className="w-16 h-16 mx-auto mb-4 opacity-30" />
                    <p>{activeTab === 'history' ? 'Belum ada riwayat PR' : 'Belum ada PR aktif'}</p>
                  </div>
                );
              }
              return (
                <div className="divide-y divide-border">
                  {filteredPRs.map((pr, idx) => (
                  <div key={idx} className="p-5" data-testid={`pr-row-${idx}`}>
                    <div className="flex flex-col sm:flex-row items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-2 flex-wrap">
                          <p className="font-medium">{pr.item_name || 'Barang'}</p>
                          <Badge className={PR_STATUSES[pr.status]?.color}>
                            {PR_STATUSES[pr.status]?.label || pr.status}
                          </Badge>
                          {!pr.ticket_id && (
                            <Badge variant="outline" className="text-xs bg-blue-50 text-blue-700 border-blue-300">
                              Restock
                            </Badge>
                          )}
                        </div>
                        {pr.ticket_id && (
                          <p className="text-sm text-muted-foreground mb-1">
                            Untuk tiket: <span className="font-mono">{pr.ticket_id}</span>
                          </p>
                        )}
                        <p className="text-sm text-muted-foreground mb-1">
                          Jumlah: <strong>{pr.quantity} {pr.unit}</strong>
                        </p>
                        {pr.vendor && <p className="text-sm text-muted-foreground">Vendor: {pr.vendor}</p>}
                        {pr.price > 0 && (
                          <p className="text-sm text-muted-foreground">
                            Harga: {formatCurrency(pr.price)} × {pr.quantity} = <strong>{formatCurrency(pr.price * pr.quantity)}</strong>
                          </p>
                        )}
                        <p className="text-xs text-muted-foreground mt-2">Alasan: {pr.reason}</p>
                        <p className="text-xs text-muted-foreground mt-1">{formatDate(pr.created_at)}</p>
                        {pr.received_at && (
                          <p className="text-xs text-emerald-700 mt-1">
                            ✓ Diterima di {pr.receive_location || 'PUSAT'}: {formatDate(pr.received_at)}
                          </p>
                        )}
                      </div>
                      <div className="flex flex-col gap-2 w-full sm:w-auto">
                        {pr.status === 'pending' && canApprovePR && (() => {
                          const blocked = isHighValuePR(pr) && !canApproveHighValue;
                          return (
                            <>
                              {isHighValuePR(pr) && (
                                <div
                                  className={`text-[11px] p-1.5 rounded border ${blocked ? 'bg-amber-50 border-amber-300 text-amber-900' : 'bg-blue-50 border-blue-200 text-blue-900'}`}
                                  data-testid={`high-value-notice-${idx}`}
                                >
                                  Nilai tinggi (&gt; {formatCurrency(HIGH_VALUE_THRESHOLD)}){blocked ? ' — perlu izin approve_high_value' : ''}
                                </div>
                              )}
                              <Button
                                size="sm"
                                className="bg-primary hover:bg-primary/90"
                                onClick={() => handleApprovePR(pr)}
                                disabled={blocked}
                                title={blocked ? 'Diperlukan izin approve_high_value untuk menyetujui PR ini' : undefined}
                                data-testid={`approve-pr-${idx}`}
                              >
                                Setujui
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                className="border-destructive text-destructive hover:bg-destructive hover:text-destructive-foreground"
                                onClick={() => handleRejectPR(pr)}
                                data-testid={`reject-pr-${idx}`}
                              >
                                Tolak
                              </Button>
                            </>
                          );
                        })()}
                        {pr.status === 'approved' && canOrderPR && (
                          <Button
                            size="sm"
                            className="bg-blue-600 hover:bg-blue-700 text-white"
                            onClick={() => handleMarkOrdered(pr)}
                            data-testid={`order-pr-${idx}`}
                          >
                            Tandai Dipesan
                          </Button>
                        )}
                        {['approved', 'ordered'].includes(pr.status) && canReceive && (
                          <Button
                            size="sm"
                            className="bg-emerald-600 hover:bg-emerald-700 text-white"
                            onClick={() => openReceiveDialog(pr)}
                            data-testid={`receive-pr-${idx}`}
                          >
                            <PackageCheck className="w-3 h-3 mr-1" />
                            Terima Barang
                          </Button>
                        )}
                        {canDeletePR(pr) && (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleDeletePR(pr)}
                            className="text-destructive hover:bg-destructive/10 hover:text-destructive h-8"
                            data-testid={`delete-pr-${idx}`}
                            title="Hapus PR"
                          >
                            Hapus
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              );
            })()}
          </CardContent>
        </Card>

        {/* Create PR Dialog */}
        <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
          <DialogContent className="max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Buat Purchase Request Baru</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Untuk Tiket (opsional)</Label>
                <Select value={createForm.ticket_id} onValueChange={(v) => setCreateForm({ ...createForm, ticket_id: v })}>
                  <SelectTrigger data-testid="pr-ticket-select">
                    <SelectValue placeholder="Kosongkan untuk restock gudang" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="_none">— Restock Gudang (tanpa tiket) —</SelectItem>
                    {tickets.filter((t) => !['completed', 'invoiced', 'rejected', 'cancelled'].includes(t.status)).map((t) => (
                      <SelectItem key={t.ticket_number} value={t.ticket_number}>
                        {t.ticket_number} — {t.title}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  Kosongkan jika ini untuk restock gudang saja (tidak terkait tiket)
                </p>
              </div>
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label>Barang *</Label>
                  <label className="flex items-center gap-2 text-xs text-muted-foreground cursor-pointer">
                    <input
                      type="checkbox"
                      checked={createForm.is_custom}
                      onChange={(e) => setCreateForm({ ...createForm, is_custom: e.target.checked, item_id: '' })}
                      className="rounded border-border"
                      data-testid="pr-custom-item-toggle"
                    />
                    Barang tidak ada di daftar? Buat baru
                  </label>
                </div>
                {createForm.is_custom ? (
                  <div className="space-y-2 border border-dashed border-border rounded-md p-3 bg-secondary/30">
                    <Input
                      placeholder="Nama barang (contoh: Kompresor AC 1.5 PK)"
                      value={createForm.custom_name}
                      onChange={(e) => setCreateForm({ ...createForm, custom_name: e.target.value })}
                      data-testid="pr-custom-name"
                    />
                    <div className="grid grid-cols-3 gap-2">
                      <Select
                        value={createForm.custom_category}
                        onValueChange={(v) => setCreateForm({ ...createForm, custom_category: v })}
                      >
                        <SelectTrigger data-testid="pr-custom-category">
                          <SelectValue placeholder="Kategori" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="elektrikal">Elektrikal</SelectItem>
                          <SelectItem value="plumbing">Plumbing</SelectItem>
                          <SelectItem value="alat">Alat</SelectItem>
                          <SelectItem value="material">Material</SelectItem>
                          <SelectItem value="ac">AC & Pendingin</SelectItem>
                          <SelectItem value="kebersihan">Kebersihan</SelectItem>
                          <SelectItem value="lainnya">Lainnya</SelectItem>
                        </SelectContent>
                      </Select>
                      <Input
                        placeholder="Satuan (pcs/kg)"
                        value={createForm.custom_unit}
                        onChange={(e) => setCreateForm({ ...createForm, custom_unit: e.target.value })}
                        data-testid="pr-custom-unit"
                      />
                      <Input
                        type="number"
                        min="0"
                        placeholder="Estimasi harga"
                        value={createForm.custom_price}
                        onChange={(e) => setCreateForm({ ...createForm, custom_price: e.target.value })}
                        data-testid="pr-custom-price"
                      />
                    </div>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={handleAiEstimate}
                      disabled={aiEstimating || !createForm.custom_name.trim()}
                      className="w-full border-primary/50 text-primary hover:bg-primary/10"
                      data-testid="pr-ai-estimate-button"
                    >
                      <Sparkles className="w-4 h-4 mr-2" />
                      {aiEstimating ? 'Menghitung...' : 'Estimasi Harga dengan AI'}
                    </Button>
                    <p className="text-xs text-muted-foreground">
                      Barang ini akan otomatis ditambahkan ke Master Barang. Harga bisa dikoreksi saat barang diterima.
                    </p>
                  </div>
                ) : (
                  <Select value={createForm.item_id} onValueChange={(v) => setCreateForm({ ...createForm, item_id: v })}>
                    <SelectTrigger data-testid="pr-item-select">
                      <SelectValue placeholder="Pilih barang" />
                    </SelectTrigger>
                    <SelectContent>
                      {items.map((item) => (
                        <SelectItem key={item.id} value={item.id}>
                          {item.name} — {formatCurrency(item.price)}/{item.unit}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </div>
              <div className="space-y-2">
                <Label>Jumlah *</Label>
                <Input
                  type="number"
                  min="1"
                  value={createForm.quantity}
                  onChange={(e) => setCreateForm({ ...createForm, quantity: e.target.value })}
                  data-testid="pr-quantity-input"
                />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div className="space-y-2">
                  <Label>Nama Vendor *</Label>
                  <Input
                    placeholder="Contoh: Toko Sinar Jaya"
                    value={createForm.vendor}
                    onChange={(e) => setCreateForm({ ...createForm, vendor: e.target.value })}
                    data-testid="pr-vendor-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Harga Estimasi Total (Rp) *</Label>
                  <Input
                    type="number"
                    min="0"
                    placeholder="Total pembelian"
                    value={createForm.estimated_price}
                    onChange={(e) => setCreateForm({ ...createForm, estimated_price: e.target.value })}
                    data-testid="pr-estimated-price-input"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Alasan Pembelian *</Label>
                <Textarea
                  placeholder="Contoh: Stok minim, untuk kebutuhan tiket X, dll"
                  value={createForm.reason}
                  onChange={(e) => setCreateForm({ ...createForm, reason: e.target.value })}
                  rows={2}
                  data-testid="pr-reason-input"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowCreateDialog(false)}>Batal</Button>
              <Button onClick={handleCreate} className="bg-primary hover:bg-primary/90" data-testid="save-pr-create">
                Buat PR
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Approve Dialog with editable qty/price */}
        <Dialog open={!!approvingPR} onOpenChange={(o) => !o && setApprovingPR(null)}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Setujui Purchase Request</DialogTitle>
            </DialogHeader>
            {approvingPR && (
              <div className="space-y-4 py-4">
                <div className="p-3 rounded-lg bg-secondary/50 border border-border">
                  <p className="text-sm font-medium">{approvingPR.item_name}</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    Diajukan oleh {approvingPR.requester_name || '-'} • Vendor: {approvingPR.vendor || '-'}
                  </p>
                  <p className="text-xs text-muted-foreground">Alasan: {approvingPR.reason}</p>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label>Jumlah *</Label>
                    <Input
                      type="number"
                      min="1"
                      value={approveForm.quantity}
                      onChange={(e) => setApproveForm({ ...approveForm, quantity: e.target.value })}
                      data-testid="approve-quantity-input"
                    />
                    <p className="text-[11px] text-muted-foreground">Semula {approvingPR.quantity} {approvingPR.unit || 'unit'}</p>
                  </div>
                  <div className="space-y-2">
                    <Label>Estimasi Total (Rp) *</Label>
                    <Input
                      type="number"
                      min="0"
                      value={approveForm.estimated_price}
                      onChange={(e) => setApproveForm({ ...approveForm, estimated_price: e.target.value })}
                      data-testid="approve-price-input"
                    />
                    <p className="text-[11px] text-muted-foreground">Semula Rp {(approvingPR.estimated_price || 0).toLocaleString('id-ID')}</p>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Catatan Approval (opsional)</Label>
                  <Textarea
                    placeholder="Catatan revisi, ex: 'jumlah dikurangi karena stok masih ada'"
                    value={approveForm.note}
                    onChange={(e) => setApproveForm({ ...approveForm, note: e.target.value })}
                    rows={2}
                    data-testid="approve-note-input"
                  />
                </div>
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setApprovingPR(null)}>Batal</Button>
              <Button onClick={submitApprove} className="bg-primary hover:bg-primary/90" data-testid="confirm-approve-button">
                Setujui PR
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Update Dialog */}
        <Dialog open={showUpdateDialog} onOpenChange={setShowUpdateDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Update Purchase Request</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Status</Label>
                <Select value={updateForm.status} onValueChange={(v) => setUpdateForm({ ...updateForm, status: v })}>
                  <SelectTrigger data-testid="pr-status-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(PR_STATUSES).filter(([k]) => k !== 'received').map(([value, { label }]) => (
                      <SelectItem key={value} value={value}>{label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">Untuk terima barang, gunakan tombol &quot;Terima Barang&quot;</p>
              </div>
              <div className="space-y-2">
                <Label>Vendor</Label>
                <Input
                  placeholder="Nama vendor"
                  value={updateForm.vendor}
                  onChange={(e) => setUpdateForm({ ...updateForm, vendor: e.target.value })}
                  data-testid="pr-vendor-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Harga Satuan (Rp)</Label>
                <Input
                  type="number"
                  min="0"
                  value={updateForm.price}
                  onChange={(e) => setUpdateForm({ ...updateForm, price: e.target.value })}
                  data-testid="pr-price-input"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowUpdateDialog(false)}>Batal</Button>
              <Button onClick={handleUpdate} className="bg-primary hover:bg-primary/90" data-testid="save-pr-button">
                Simpan
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Receive Dialog */}
        <ReceivePRDialog
          open={showReceiveDialog}
          onOpenChange={setShowReceiveDialog}
          selectedPR={selectedPR}
          receiveForm={receiveForm}
          setReceiveForm={setReceiveForm}
          locations={locations}
          onUpload={handleUploadReceiveReceipt}
          uploadingReceipt={uploadingReceipt}
          onSubmit={handleReceive}
        />
      </div>
    </Layout>
  );
};

export default PurchasingPage;
